import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Position } from '../../../shared/types/protocol.ts';
import { getPositionSymbolFinal } from '../../../shared/utils/getPositionSymbolFinal.ts';
import { isOutOfSpec } from '../../../shared/utils/isOutOfSpec.ts';
import { getUsedTool } from '../../services/getUsedTool.ts';
import { CustomEquipment, RecordPosition } from '../../types/sharedTypeImpl.ts';
import { evalOrNormalize } from '../../utils/positionInputParse.ts';
import { getDeviation } from './getDeviation.ts';


interface MeasurementPositionProps {
    position: Position,
    recordPos: RecordPosition,
    onUpdatePositionValue: (positionNumber: string, value: string) => void,
    isActive: boolean,
    isViewMode: boolean,
    cmmEquipments: CustomEquipment[] | undefined,
    onPositionFocus: (positionNumber: string) => void,
}

export function MeasurementPosition({
    position,
    recordPos,
    onUpdatePositionValue,
    isActive,
    isViewMode,
    cmmEquipments,
    onPositionFocus,
}: MeasurementPositionProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [deviation, setDeviation] = useState<string>(getDeviation(recordPos, position));

    const isAuto = position.toolType === 'CMM' || position.toolType === 'VMM';
    const rowClass = classNames({
        'highlight--selected': isActive && !isAuto,
        'automatic': !isActive && isAuto,
        'automatic--selected': isActive && isAuto,
    });

    const outOfSpec = isOutOfSpec(position, recordPos);
    const hasFocus = isActive && !isViewMode;
    const highlightError = outOfSpec && !hasFocus;


    useEffect(() => {
        // Update deviation if not currently being edited. Also updates when a value is imported.
        if (!hasFocus) {
            setDeviation(getDeviation(recordPos, position));
        }
    }, [recordPos, position, hasFocus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (/^[0-9.,*/+\-()]*$/.test(e.target.value)) {
            const valueNoCommas = e.target.value.replace(/,/g, '.');
            onUpdatePositionValue(position.positionNumber, valueNoCommas);

            // Maintain cursor position after removing commas
            const valueChanged = valueNoCommas !== e.target.value;
            const cursorPos = e.target.selectionStart;
            requestAnimationFrame(() => {
                if (inputRef.current && cursorPos !== null && valueChanged) {
                    inputRef.current.setSelectionRange(cursorPos, cursorPos);
                }
            });
        }
    }

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newValue = evalOrNormalize(value);
        onUpdatePositionValue(position.positionNumber, newValue);
    }


    return (
        <tr
            className={rowClass}
            onClick={() => onPositionFocus(position.positionNumber)}
        >
            <td>#<b>{`${position.positionNumber}`}</b></td>
            <td>
                <input
                    ref={inputRef}
                    className={highlightError ? 'position-field--out-of-spec' : ''}
                    style={{ pointerEvents: isViewMode ? 'none' : 'auto', }}//Let clicks pass through when disabled
                    type='text'
                    value={(recordPos.value as string | null) ?? ''}
                    onFocus={() => onPositionFocus(position.positionNumber)}
                    onBlur={handleInputBlur}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    {...(isAuto && { tabIndex: -1 })}
                />
            </td>
            <td>{position.nominal} {getPositionSymbolFinal(position)}</td>
            <td>{position.upperTolerance}</td>
            <td>{position.lowerTolerance}</td>
            <td>{deviation}</td>
            <td>{isViewMode ? getUsedTool(recordPos, position, cmmEquipments) : position.toolType}</td>
        </tr>
    );
}
